import { useEffect } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';

import { Button } from 'shared/components/Button';
import { Modal } from 'shared/components/Modal';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { useAccount } from 'account/hooks/useAccount';
import { t } from 'localization/utils/t';

const TP = 'checkout.components.ErrorMessageModal';

export const ErrorMessageModal = () => {
  const { errorModalMessage, dismissGlobalError, isErrorUnrecoverable } = useCheckoutContext();
  const router = useRouter();

  const { logOutAccount } = useAccount();

  const isUnavailableInRegion =
    errorModalMessage ===
    t(
      `${TP}.errorModalMessagem`,
      'This item is currently unavailable for purchase in your region.',
    );

  const formattedErrorMessage = (() => {
    switch (errorModalMessage) {
      case `The billing zip code provided is incorrect.`:
        return t(
          `${TP}.stripeZipCodeError`,
          `Your billing address zip code must match the zip code associated with your payment method.`,
        );
      case `Your card's security code is incorrect.`:
        return t(`${TP}.stripeSecurityCodeError`, `The CVV you entered is incorrect.`);
      default:
        return errorModalMessage;
    }
  })();

  useEffect(() => {
    if (errorModalMessage) {
      sendTrackingEvent('MODAL_OPEN_VIEW', {
        modal_name: 'checkout_error',
      });
    }
  }, [errorModalMessage]);

  if (!errorModalMessage) {
    return null;
  }

  const handleCtaClick = async () => {
    dismissGlobalError();

    if (isErrorUnrecoverable) {
      await logOutAccount();
      router.back();
    }
  };

  return (
    <ErrorMessageModal.Modal
      data-qa="checkout_error_modal"
      onClose={dismissGlobalError}
      title={isUnavailableInRegion ? '' : t(`${TP}.errorModalTitle`, 'Please Try Again')}
    >
      <ErrorMessageModal.Message>
        {formattedErrorMessage ||
          t(
            `${TP}.defaultErrorModalMsg`,
            'We could not proccess your order. Please try again or contact GOAT support for assistance.',
          )}
      </ErrorMessageModal.Message>
      <ErrorMessageModal.Button
        data-qa="checkout_error_modal_cta_button"
        onClick={handleCtaClick}
        title={
          isErrorUnrecoverable
            ? t(`${TP}.backToProductButton`, 'Back to Product')
            : t(`${TP}.confirmButton`, 'Okay')
        }
      />
    </ErrorMessageModal.Modal>
  );
};

ErrorMessageModal.Modal = styled(Modal)`
  width: 335px;
`;

ErrorMessageModal.Button = styled(Button)`
  height: 32px;
`;

ErrorMessageModal.Message = styled.div`
  padding-top: 8px;
  padding-bottom: 32px;
  font-weight: 400;
  font-size: 11.5px;
`;

ErrorMessageModal.InstantFee = styled.div`
  padding-top: 14px;
  padding-bottom: 24px;
`;
